import { Flex } from '@abyss/web/ui/Flex';
import { Print } from '@abyss/web/ui/Print';
import { format } from 'date-fns';
import find from 'lodash/find';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  CareCategories,
  ClientNames,
  Constants,
} from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { PremiumCareDisclaimer } from '../../../../../common/DisclaimersContainer/PremiumCareDisclaimer';
import { useHarvardPilgrimEligibility } from '../../../../../common/GuestHeader/utils';
import { PortalIdentifier, PortalName } from '../../../../../common/Portal';
import { PSXIcon } from '../../../../../common/PSXIcon';
import { getFeatureFlag } from '../../../../../common/Utils';
import {
  isCnsMember,
  isMnrMember,
} from '../../../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../../../context/PortalContext';
import { useCustomDisclaimerMessage } from '../../../../../hooks/useCustomDisclaimerMessage';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../../hooks/useSmartChoice';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../../store/useTypeaheadStore/typeaheadStore';
import { DateTime } from '../../../../../utils/DateTime';
import { returnInclusionFlag } from '../../../../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../../../../utils/gehaThemeUtils';
import { getCurrentMember } from '../../../../../utils/user.utils';
import { getGuestPlanSelectionData } from '../../../../GuestExperience/Guest.utils';
import BehavioralHealthFacilityPrintView from './BehavioralHealthFacilityPrintView';
import BehavioralHealthProviderPrintView from './BehavioralHealthProviderPrintView';
import {
  PrintViewDivider,
  PrintViewProviderBoldText,
  PrintViewProviderMediumText,
  PrintViewProviderNormalText,
  StyledMarkdown,
} from './PrintView.style';
import { ProviderDemographics } from './ProviderDemographics';
import { ProviderDetails } from './ProviderDetails';

type Props = {
  selectedProviderList: {
    numberOfReviews?: string;
    acceptingNewPatients: boolean;
    providerName: string;
    providerId: string;
    primaryDegrees: string[];
    speciality: string;
    organizationType: string;
    providerType?: string;
    networkStatus: string;
    distance: string;
    isPreferredLab?: boolean;
    isFreeStandingFacility?: boolean;
    ddpCode?: string;
    healthGradeRating?: string;
    isTieredProvider: boolean;
    isTier2Provider: boolean;
    virtualCareOffered?: boolean;
    languagesSpoken: string[];
    website: string;
    address: {
      postalCode: string;
      district: string;
      line: string[];
      state: string;
      city: string;
    };
    firstName?: string;
    middleName?: string;
    lastName?: string;
    costIndicator?: string;
    phones: {
      phone: string[];
    };
  }[];
};

export const PrintView = ({ selectedProviderList }: Props) => {
  const { search, sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );
  const { chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      chipValue: state.chipValue,
    }))
  );

  const printViewHeading = () => {
    switch (sectionType?.toLowerCase()) {
      case Constants.RESULT_SECTION.PROVIDER.toLowerCase():
        return t('SHARE_PROVIDERS.PROVIDER');
      case Constants.RESULT_SECTION.FACILITY.toLowerCase():
        return t('SHARE_PROVIDERS.FACILITY');
      case Constants.RESULT_SECTION.PROVIDER_GROUPS.toLowerCase():
        return t('SHARE_PROVIDERS.PROVIDER_GROUP');
      default:
        return t(sectionType || '');
    }
  };

  const { t } = useTranslation();
  const pageHeader = `${printViewHeading()} ${t(`${search || ''}`)}`;
  const theDate = () => {
    const rawDate = format(DateTime.getCurrentDateTime(), 'MMMM dd, yyyy');
    const dateSplit = rawDate.split(' ');
    const parsedTranslatedMonth = t(dateSplit[0]);
    return ` ${parsedTranslatedMonth} ${dateSplit[1]} ${dateSplit[2]}`;
  };

  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();

  const disclaimerInPage = find(uiMessaging, {
    key: ConstantsLagoon.HEALTHGRADES.DISCLAIMER_IN_PAGE,
  });
  const customDisclaimerInPageMessage = useCustomDisclaimerMessage(
    Constants.CUSTOM_DISCALIMER_BANNER_PAGE_NAME.HEALTH_GRADES_IN_PAGE
  );
  const disclaimerInPageMessage =
    customDisclaimerInPageMessage ?? disclaimerInPage?.message ?? '';

  const healthGradesDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.HEALTHGRADES.PRINT_VIEW_CONTENT,
  });
  const customHealthGradesDisclaimerMessage = useCustomDisclaimerMessage(
    Constants.CUSTOM_DISCALIMER_BANNER_PAGE_NAME
      .HEALTH_GRADES_PRINT_VIEW_CONTENT
  );
  const healthGradesDisclaimerMessage =
    customHealthGradesDisclaimerMessage ??
    healthGradesDisclaimer?.message ??
    '';

  const cnsDisclaimerForNotInclusionPolicyIds = find(uiMessaging, {
    key: ConstantsLagoon.HEALTHGRADES.CNS_DISCLAIMER_FOR_NOT_INCLUSION_STATES,
  });

  const dentalMnrDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.MNR_DISCLAIMER.DENTAL_MNR_DISCLAIMER,
  });

  const medicalFacilityMnrDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.MNR_DISCLAIMER.MEDICAL_FACILITY_MNR_DISCLAIMER,
  });

  const visionMnrDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.MNR_DISCLAIMER.VISION_MNR_DISCLAIMER,
  });

  const directoryMnrDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.MNR_DISCLAIMER.DIRECTORY_MNR_DISCLAIMER,
  });

  const visionDirectoryDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.VISION_DICTIONARY.DISCLAIMER_CONTENT,
  });

  const visionDirectoryDisclaimerAddlContent = find(uiMessaging, {
    key: ConstantsLagoon.VISION_DICTIONARY.DISCLAIMER_ADDL_CONTENT,
  });

  const smartChoiceDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_DISCLAIMER,
  });

  const bhFacilityDisclaimer = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.BH_FACILITY_PDF_PRINT_DISCLAIMER,
  });

  const currentMember = getCurrentMember();

  const lob = currentMember?.lineOfBusiness;
  const isMNR = isMnrMember(lob);
  const isCNS = isCnsMember(lob);
  const includeDocAsap =
    isCNS &&
    returnInclusionFlag(
      ConstantsLagoon.FEATURE_INCLUSION_FLAGS.INCLUDE_DOC_ASAP
    );

  const renderDisclaimer = () => {
    if (isMNR) {
      switch (chipValue) {
        case CareCategories.DENTAL:
          return dentalMnrDisclaimer?.message.replace(/\n/g, '<br/>');
        case CareCategories.MEDICAL_SPECIALISTS:
        case CareCategories.FACILITIES:
          return medicalFacilityMnrDisclaimer?.message.replace(/\n/g, '<br/>');
        case CareCategories.ALL:
          return directoryMnrDisclaimer?.message.replace(/\n/g, '<br/>');
        case CareCategories.VISION:
          return visionMnrDisclaimer?.message.replace(/\n/g, '<br/>');
        default:
          return '';
      }
    }

    if (isCNS && !includeDocAsap) {
      return cnsDisclaimerForNotInclusionPolicyIds?.message;
    }

    return chipValue === Constants.CHIPS_CATEGORIES.VISION
      ? `${disclaimerInPageMessage.replace(/\n/g, '<br/>')}
                <br><br/>
                ${visionDirectoryDisclaimer?.message ?? ''}
                <br><br/>
                ${visionDirectoryDisclaimerAddlContent?.message ?? ''}`
      : healthGradesDisclaimerMessage;
  };

  const { portalData } = useContext(PortalContext);
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;
  const isLaww =
    portalData?.portalName === PortalName.LAWW ||
    portalData?.portalName === PortalName.LAWW_GUEST;
  const isGuestUser = portalData?.portalConfig?.isGuestUser;

  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );
  const isEmpireGuest =
    isGuestUser && clientName?.toLowerCase() === ClientNames.EMPIRE;
  const guestPlanSelectionData = getGuestPlanSelectionData();
  const portalDelsysCode = guestPlanSelectionData?.selectedPlan?.planIdentifier;

  let brandConfig;

  const [isHarvardPilgrimLogoEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_HARVARD_PILGRIM_LOGO,
  ]);
  const isHarvardPilgrim =
    isHarvardPilgrimLogoEnabled &&
    useHarvardPilgrimEligibility(portalDelsysCode);

  if (isLaww) {
    brandConfig = {
      affiliate: 'optum',
      name: 'optum',
      color: 'orange',
      icon: 'optum_logo',
    };
  } else {
    brandConfig = {
      affiliate: 'uhc',
      name: 'uhc',
      color: 'black',
      icon: 'uhc_logo',
    };
  }

  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;

  const renderSmartChoiceDisclaimer = () => (
    <>
      <StyledMarkdown>{smartChoiceDisclaimer?.message}</StyledMarkdown>
      <br />
    </>
  );

  const enableEnhancedPdfPrintBhFacility: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ENHANCED_PDF_PRINT_BH_FACILITY
  );

  const enableEnhancedPdfPrintBhProvider: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ENHANCED_PDF_PRINT_BH_PROVIDERS
  );

  const isBehavioralHealthFacility =
    chipValue === Constants.CHIPS_CATEGORIES.BEHAVIORAL_HEALTH &&
    sectionType === 'Facility';

  const isBehavioralHealthProvider =
    chipValue === Constants.CHIPS_CATEGORIES.BEHAVIORAL_HEALTH &&
    sectionType === 'Provider';

  const shouldEnableEnhancedPdfPrintBhFacility =
    isBehavioralHealthFacility && enableEnhancedPdfPrintBhFacility;

  const shouldEnableEnhancedPdfPrintBhProvider =
    isBehavioralHealthProvider && enableEnhancedPdfPrintBhProvider;

  const disclaimerMarginLeft = shouldEnableEnhancedPdfPrintBhFacility
    ? '0px'
    : '25px';
  const pdfPrintWidth = shouldEnableEnhancedPdfPrintBhFacility ? '100%' : '93%';
  const pdfPrintDisclaimerWidth = shouldEnableEnhancedPdfPrintBhFacility
    ? '100%'
    : '95%';

  return (
    <Print.Section
      css={{
        border: 'none',
        padding: '30px 30px 30px 30px',
        fontFamily: '$primary',
      }}
    >
      <Flex alignContent="flex-start" direction="column">
        <Flex justify="space-between">
          {isHarvardPilgrim ? (
            <PSXIcon icon="hphc_logo" />
          ) : isGehaTheme ? (
            <PSXIcon
              css={{ height: '52px' }}
              icon={isGehaTheme ? 'geha_brandingV2' : 'geha_branding'}
              size={200}
            />
          ) : isEmpireGuest ? (
            <PSXIcon icon="empire_logo" />
          ) : (
            <PSXIcon color={brandConfig?.color} icon={brandConfig?.icon} />
          )}
        </Flex>

        <PrintViewDivider orientation="horizontal" />
        <Flex justify="space-between">
          <Flex>
            <PrintViewProviderMediumText color="#000000">
              {pageHeader}
            </PrintViewProviderMediumText>
          </Flex>
          <Flex>
            <PrintViewProviderBoldText color="#000000">
              {t('SHARE_PDF.Date')}:&nbsp;
            </PrintViewProviderBoldText>
            <PrintViewProviderNormalText color="#000000">
              {theDate()}
            </PrintViewProviderNormalText>
          </Flex>
        </Flex>
        <PrintViewDivider orientation="horizontal" />
        {selectedProviderList?.map((provider, index) => (
          <React.Fragment key={provider.providerId}>
            {/** ***** First detail row (Number label, Provider Name, Network Status) ****** */}
            <Flex
              css={{
                'abyss-flex-root': {
                  gap: '4px',
                  width: '100%',
                },
              }}
            >
              {!shouldEnableEnhancedPdfPrintBhFacility && (
                <span
                  style={{
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    background: 'black',
                    fontSize: '14px',
                    lineHeight: '16.71px',
                    border: '2px solid #666',
                    color: 'white',
                    textAlign: 'center',
                    fontWeight: '500',
                  }}
                >
                  {index + 1}
                </span>
              )}
              <Flex
                css={{
                  'abyss-flex-root': {
                    gap: '2px',
                    padding: 0,
                    flexWrap: 'nowrap',
                    width: pdfPrintWidth,
                  },
                }}
                direction="column"
              >
                {shouldEnableEnhancedPdfPrintBhFacility ? (
                  <BehavioralHealthFacilityPrintView
                    index={index}
                    portalData={portalData}
                    provider={provider}
                  />
                ) : shouldEnableEnhancedPdfPrintBhProvider ? (
                  <BehavioralHealthProviderPrintView
                    // index={index}
                    portalData={portalData}
                    provider={provider}
                  />
                ) : (
                  <Flex justify="space-between">
                    {/* left side information */}
                    <ProviderDemographics provider={provider} />
                    {/* right side information */}
                    <ProviderDetails provider={provider} />
                    <PrintViewDivider orientation="horizontal" />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </React.Fragment>
        ))}
      </Flex>
      <Flex
        alignContent="center"
        css={{
          marginLeft: disclaimerMarginLeft,
          display: 'flex',
          width: pdfPrintDisclaimerWidth,
        }}
      >
        <Flex
          alignContent="flex-start"
          css={{
            display: 'flex',
            color: '#333',
            fontFamily: '$primary',
            fontSize: '9px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: 'normal',
          }}
          direction="column"
        >
          {shouldEnableEnhancedPdfPrintBhFacility && (
            <span
              style={{
                marginTop: '10px',
                marginBottom: '10px',
              }}
            >
              {bhFacilityDisclaimer?.message}
            </span>
          )}

          {/* Lagoon data disclaimer */}
          <div
            dangerouslySetInnerHTML={{
              __html: renderDisclaimer(),
            }}
          />
          {isSmartChoice && renderSmartChoiceDisclaimer()}
          <PremiumCareDisclaimer
            isPDFView={true}
            showPremiumCareDisclaimer={true}
          />
        </Flex>
      </Flex>
    </Print.Section>
  );
};
